import { Formsy, george, pr, theme } from "aaa";
import { InfoBoxWithBackground } from "components/common/InfoBoxWithBackground";
import { ui } from "components/util/uiHelpers";
import { observer } from "mobx-react";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { getDefaultKeyPressHandler } from "util/helpers";
import { CardTanSettingsRoutes } from "./CardTanSettingsRoutes";
import { CardTanSettingsStore } from "./CardTanSettingsStore";

const initialPinConfig = {
    new: false,
    old: false,
    repeat: false
};

const adornmentStyle: React.CSSProperties = {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    color: "#135EE2",
    userSelect: "none"
};

const VisibilityToggleIcon = ({ isVisible, onClick }: { isVisible?: boolean, onClick: () => void }) => (
    isVisible
        ? <pr.IconShowOff onClick={onClick} style={adornmentStyle} />
        : <pr.IconShowOn onClick={onClick} style={adornmentStyle} />
);

export const CardTanSettingsPin = observer(({ store }: { store: CardTanSettingsStore }) => {
    const [showPinConfig, setShowPinConfig] = React.useState(initialPinConfig);
    const [validationError, setValidationError] = React.useState<string | undefined>(undefined);
    const [didAutofocus, setDidAutofocus] = React.useState(false);

    const labelStyle = {
        color: ui.isDarkMode ? "white" : "#0A285C",
        lineHeight: "20px",
        fontWeight: 600,
        marginBottom: 4
    };

    const inputStyle = {
        color: ui.isDarkMode ? "white" : "#0A285C",
        fontSize: 18
    };

    const inputFrameStyle = ui.isDarkMode ? {
        // unset the height in order to make the lineHeight prop work
        height: "unset",
        paddingLeft: 16,
        paddingRight: 56,
    } : {
            backgroundColor: "white",
            border: "none",
            // unset the height in order to make the lineHeight prop work
            height: "unset",
            paddingLeft: 16,
            paddingRight: 56,
        };

    const handleChangeOldPin = (value: string) => {
        setValidationError(undefined);
        store.oldPin = value;
    };

    const handleChangeNewPin = (value: string) => {
        setValidationError(undefined);
        store.newPin = value;
    };

    const handleChangeNewPinRepeat = (value: string) => {
        setValidationError(undefined);
        store.newPinRepeat = value;
    };

    const handleTogglePinVisibility = (key: keyof typeof initialPinConfig) => {
        setShowPinConfig(config => ({
            ...config,
            [key]: !config[key]
        }));
    };

    const handleSubmitNewPin = async () => {
        try {
            await store.changePin();

            pushRoute(CardTanSettingsRoutes.CHANGE_PIN_SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    const handleBack = async () => {
        store.resetPinValues();
        pushRoute(CardTanSettingsRoutes.ROOT);
    };

    const ref = React.useRef<HTMLAnchorElement | null>(null);

    React.useEffect(() => {
        if (ref.current && !didAutofocus) {
            ref.current.focus();
            setDidAutofocus(true);
        }
    });

    if (store.isLoading) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    const getErrorMessage = (variant: "new" | "old" | "repeat") => {
        if (variant === "old" && store.oldPin.length === 0) {
            return ui.__("cardTanSettings.pin.validation.oldPin.empty");
        } else if (variant === "new" && store.newPin.length === 0) {
            return ui.__("cardTanSettings.pin.validation.newPin.empty");
        } else if (variant === "new" && !new RegExp(`^[0-9]+$`).test(store.newPin)) {
            return ui.__("cardTanSettings.pin.validation.characters");
        } else if (variant === "new" && store.newPin.length !== 5) {
            return ui.__("cardTanSettings.pin.validation.length");
        } else if (variant === "new" && store.newPin === store.oldPin) {
            return ui.__("cardTanSettings.pin.validation.newPin.matchesOldPin");
        } else if (variant === "repeat" && store.newPinRepeat.length === 0) {
            return ui.__("cardTanSettings.pin.validation.newPinRepeat.empty");
        } else if (variant === "repeat" && store.newPin !== store.newPinRepeat) {
            return ui.__("cardTanSettings.pin.validation.newPinRepeat.notMatching");
        } else if (validationError) {
            return validationError;
        }
    };

    return (
        <george.Background>
            <george.PageWithActionButton>
                <div style={{ padding: theme.MARGIN }}>
                    <a
                        ref={ref}
                        style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onClick={handleBack}
                        onKeyPress={getDefaultKeyPressHandler(handleBack)}
                        aria-label={ui.__("a11y.back")}
                    >
                        <pr.IconChevronLeft style={{ color: "#135EE2" }} />
                        {!externalInterfaceStore.isGeorgeApp && (
                            <pr.P_16_CONTENT style={{ color: "#135EE2", fontWeight: 600 }}>
                                {ui.__("common.back")}
                            </pr.P_16_CONTENT>
                        )}
                    </a>
                    <pr.P_24_TITLE style={{ fontWeight: 700, marginTop: theme.MARGIN }}>
                        {ui.__("cardTanSettings.setting.pin")}
                    </pr.P_24_TITLE>
                    <pr.P_16_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C", lineHeight: "22px", marginTop: 16 }}>
                        {ui.__("cardTanSettings.pin.description")}
                    </pr.P_16_CONTENT>
                    <Formsy.Form>
                        <pr.P_14_CONTENT style={{ ...labelStyle, marginTop: 32 }}>
                            {ui.__("cardTanSettings.pin.oldPin")}
                        </pr.P_14_CONTENT>
                        <george.TextFieldFormsy
                            name="oldPin"
                            required
                            type={showPinConfig.old ? "input" : "password"}
                            validateEmpty
                            getErrorMessage={() => getErrorMessage("old")}
                            hintText={ui.__("cardTanSettings.pin.oldPin")}
                            onChange={handleChangeOldPin}
                            inputStyle={inputStyle}
                            lineHeight={32}
                            inputFrameStyle={inputFrameStyle}
                            value={store.oldPin}
                            aria-label={ui.__("cardTanSettings.pin.oldPin")}
                            maxLength={80}
                            adornment={<VisibilityToggleIcon isVisible={showPinConfig.old} onClick={() => handleTogglePinVisibility("old")} />}
                        />
                        <pr.P_14_CONTENT style={{ ...labelStyle, marginTop: 16 }}>
                            {ui.__("cardTanSettings.pin.newPin")}
                        </pr.P_14_CONTENT>
                        <george.TextFieldFormsy
                            name="newPin"
                            required
                            type={showPinConfig.new ? "input" : "password"}
                            validateEmpty
                            getErrorMessage={() => getErrorMessage("new")}
                            hintText={ui.__("cardTanSettings.pin.newPin")}
                            onChange={handleChangeNewPin}
                            inputStyle={inputStyle}
                            lineHeight={32}
                            inputFrameStyle={inputFrameStyle}
                            value={store.newPin}
                            aria-label={ui.__("cardTanSettings.pin.newPin")}
                            maxLength={80}
                            adornment={<VisibilityToggleIcon isVisible={showPinConfig.new} onClick={() => handleTogglePinVisibility("new")} />}
                        />
                        <pr.P_14_CONTENT style={{ ...labelStyle, marginTop: 16 }}>
                            {ui.__("cardTanSettings.pin.newPinRepeat")}
                        </pr.P_14_CONTENT>
                        <george.TextFieldFormsy
                            name="newPinRepeat"
                            required
                            type={showPinConfig.repeat ? "input" : "password"}
                            validateEmpty
                            getErrorMessage={() => getErrorMessage("repeat")}
                            hintText={ui.__("cardTanSettings.pin.newPinRepeat")}
                            onChange={handleChangeNewPinRepeat}
                            inputStyle={inputStyle}
                            lineHeight={32}
                            inputFrameStyle={inputFrameStyle}
                            value={store.newPinRepeat}
                            aria-label={ui.__("cardTanSettings.pin.newPinRepeat")}
                            maxLength={80}
                            adornment={<VisibilityToggleIcon isVisible={showPinConfig.repeat} onClick={() => handleTogglePinVisibility("repeat")} />}
                        />
                    </Formsy.Form>
                    <InfoBoxWithBackground iconSize={16} style={{ marginTop: 32 }}>
                        {ui.__("cardTanSettings.pin.infoText")}
                    </InfoBoxWithBackground>
                </div>
                <george.ActionButton
                    onClick={handleSubmitNewPin}
                    primary
                    messageId="common.save"
                    disableButton={!!getErrorMessage("new") || !!getErrorMessage("old") || !!getErrorMessage("repeat")}
                />
            </george.PageWithActionButton>
        </george.Background>
    );
});


// WEBPACK FOOTER //
// ./src/components/loginManagement/cardTanSettings/CardTanSettingsPin.tsx